import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Search, ShieldCheck, ShieldOff, UserCog } from "lucide-react";
import { api, fmtDate, apiError } from "@/lib/api";

const ROLE_META = {
  owner: { label: "Owner", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  admin: { label: "Admin", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  customer: { label: "Customer", cls: "bg-white/[0.06] text-neutral-400 border-white/10" },
};

export default function AdminTeam() {
  const [users, setUsers] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users")
      .then(({ data }) => setUsers(data.users))
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error(apiError(e, "Couldn't load users"));
      });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (user, role) => {
    if (busyId) return;
    const verb = role === "admin" ? "Make admin" : "Remove admin access for";
    if (!window.confirm(`${verb} ${user.name} (${user.email})?`)) return;
    setBusyId(user.id);
    try {
      await api.put(`/admin/users/${user.id}/role`, { role });
      toast.success(role === "admin" ? `${user.name} is now an admin` : `${user.name} is back to customer`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update the role"));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [users, query]);

  const counts = useMemo(() => ({
    owner: (users || []).filter((u) => u.role === "owner").length,
    admin: (users || []).filter((u) => u.role === "admin").length,
    customer: (users || []).filter((u) => u.role === "customer").length,
  }), [users]);

  if (forbidden) {
    return (
      <div data-testid="team-forbidden" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center">
        <Crown className="mx-auto mb-3 h-8 w-8 text-amber-500/60" />
        <p className="text-sm font-semibold text-white">Owner access only</p>
        <p className="mt-1 text-xs text-neutral-500">Only the account owner can manage team roles.</p>
      </div>
    );
  }

  return (
    <div data-testid="team-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><UserCog className="h-6 w-6 text-amber-400" /> Team & Access</h1>
          <p className="mt-1 text-sm text-neutral-500">Grant or revoke admin access for any account. The owner role can't be changed here.</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email…" data-testid="team-search"
            className="h-10 w-72 rounded-xl border border-white/10 bg-black/40 pl-10 pr-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="team-counts">
        {Object.entries(ROLE_META).map(([r, meta]) => (
          <span key={r} className={`rounded-full border px-3 py-1 text-[11px] font-bold ${meta.cls}`}>
            {meta.label}s: {counts[r]}
          </span>
        ))}
      </div>

      {!users ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="team-empty" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500">
          No users match that search.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/[0.07]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Access</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const role = u.role || "customer";
                const meta = ROLE_META[role] || ROLE_META.customer;
                const isOwner = role === "owner";
                return (
                  <tr key={u.id} data-testid={`team-row-${u.id}`} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" referrerPolicy="no-referrer" />
                        ) : (
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-300">
                            {(u.name || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <div>
                          <p className="font-semibold text-white">{u.name}</p>
                          <p className="text-xs text-neutral-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span data-testid={`team-role-${u.id}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${meta.cls}`}>
                        {isOwner && <Crown className="h-3 w-3" />}
                        {meta.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-neutral-500">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {isOwner ? (
                        <span className="text-[11px] font-semibold text-neutral-600">Locked</span>
                      ) : role === "admin" ? (
                        <button onClick={() => setRole(u, "customer")} disabled={busyId === u.id} data-testid={`team-demote-${u.id}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-2 text-xs font-bold text-red-300 transition-colors hover:bg-red-500/20 disabled:opacity-40">
                          {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />}
                          Remove Admin
                        </button>
                      ) : (
                        <button onClick={() => setRole(u, "admin")} disabled={busyId === u.id} data-testid={`team-promote-${u.id}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:opacity-40">
                          {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                          Make Admin
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
